// ============================================
// Underwater Post-Process Effect
// ============================================
// Refraction waves + Voronoi caustics + water-color absorption.

import type { IRenderContext } from '../../core/IRenderContext';
import {
  PostProcessEffect,
  type PostProcessContext,
  type PostProcessParameter,
} from '../PostProcessStack';
import { UNDERWATER_LAYOUT } from '../PostProcessLayouts';
import {
  type PipelineHandle,
  type ShaderHandle,
  NULL_HANDLE,
} from '../../types';
import { getCompiledShader } from '../ShaderCache';

/**
 * Underwater post-processing effect
 */
export class UnderwaterEffect extends PostProcessEffect {
  readonly type = 'underwater';
  readonly displayName = 'Underwater';

  // Parameters
  waveStrength = 4.0;
  waveScale = 2.0;
  causticsIntensity = 0.6;
  causticsScale = 6.0;
  speed = 1.0;
  tintStrength = 0.5;
  tintR = 0.2;
  tintG = 0.6;
  tintB = 0.8;

  // GPU resources
  private shader: ShaderHandle = NULL_HANDLE;
  private pipelineHDR: PipelineHandle = NULL_HANDLE;
  private pipelineLDR: PipelineHandle = NULL_HANDLE;
  private uniformWriter = UNDERWATER_LAYOUT.createWriter();

  getParameters(): PostProcessParameter[] {
    return [
      { name: 'waveStrength', type: 'float', min: 0, max: 10, default: 4.0, description: 'Refraction strength' },
      { name: 'waveScale', type: 'float', min: 0.5, max: 10, default: 2.0, description: 'Wave scale' },
      { name: 'causticsIntensity', type: 'float', min: 0, max: 3, default: 0.6, description: 'Caustics brightness' },
      { name: 'causticsScale', type: 'float', min: 1, max: 20, default: 6.0, description: 'Caustics density' },
      { name: 'speed', type: 'float', min: 0, max: 5, default: 1.0, description: 'Animation speed' },
      { name: 'tintStrength', type: 'float', min: 0, max: 1, default: 0.5, description: 'Water absorption' },
      { name: 'tintR', type: 'float', min: 0, max: 1, default: 0.2, description: 'Water R' },
      { name: 'tintG', type: 'float', min: 0, max: 1, default: 0.6, description: 'Water G' },
      { name: 'tintB', type: 'float', min: 0, max: 1, default: 0.8, description: 'Water B' },
    ];
  }

  getParameterValue(name: string): number | boolean | number[] {
    switch (name) {
      case 'waveStrength': return this.waveStrength;
      case 'waveScale': return this.waveScale;
      case 'causticsIntensity': return this.causticsIntensity;
      case 'causticsScale': return this.causticsScale;
      case 'speed': return this.speed;
      case 'tintStrength': return this.tintStrength;
      case 'tintR': return this.tintR;
      case 'tintG': return this.tintG;
      case 'tintB': return this.tintB;
      default: return 0;
    }
  }

  setParameterValue(name: string, value: number | boolean | number[]): void {
    if (typeof value !== 'number') return;
    switch (name) {
      case 'waveStrength': this.waveStrength = Math.max(0, Math.min(20, value)); break;
      case 'waveScale': this.waveScale = Math.max(0.1, Math.min(20, value)); break;
      case 'causticsIntensity': this.causticsIntensity = Math.max(0, Math.min(5, value)); break;
      case 'causticsScale': this.causticsScale = Math.max(0.5, Math.min(40, value)); break;
      case 'speed': this.speed = Math.max(0, Math.min(10, value)); break;
      case 'tintStrength': this.tintStrength = Math.max(0, Math.min(1, value)); break;
      case 'tintR': this.tintR = Math.max(0, Math.min(1, value)); break;
      case 'tintG': this.tintG = Math.max(0, Math.min(1, value)); break;
      case 'tintB': this.tintB = Math.max(0, Math.min(1, value)); break;
    }
  }

  protected onInitialize(ctx: IRenderContext): void {
    const hdrFormat = 'rgba16float' as const;
    const ldrFormat = ctx.getSwapChainFormat();

    const underwaterWgsl = getCompiledShader('postprocess/underwater');
    this.shader = ctx.createShader(underwaterWgsl, underwaterWgsl);

    this.pipelineHDR = ctx.createPipeline({
      shader: this.shader,
      vertexBuffers: [],
      colorTargets: [{ format: hdrFormat }],
      primitive: { topology: 'triangle-list', cullMode: 'none', usesTextures: true, textureCount: 1 },
      label: 'underwater-hdr',
    });
    this.pipelineLDR = ctx.createPipeline({
      shader: this.shader,
      vertexBuffers: [],
      colorTargets: [{ format: ldrFormat }],
      primitive: { topology: 'triangle-list', cullMode: 'none', usesTextures: true, textureCount: 1 },
      label: 'underwater-ldr',
    });
  }

  protected onRender(context: PostProcessContext): void {
    const { ctx, source, width, height, time, intensity, isHdrTarget } = context;

    // NOTE: PostProcessStack already set render target and viewport

    this.uniformWriter.setAll({
      waveStrength: this.waveStrength * intensity,
      waveScale: this.waveScale,
      causticsIntensity: this.causticsIntensity * intensity,
      causticsScale: this.causticsScale,
      speed: this.speed,
      tintStrength: this.tintStrength * intensity,
      // Wrapped so the noise scroll keeps float precision on long sessions.
      time: time % 256.0,
      aspect: width / Math.max(height, 1),
      tint: [this.tintR, this.tintG, this.tintB],
      _pad: 0,
    });
    this.fullscreenPass(ctx, isHdrTarget ? this.pipelineHDR : this.pipelineLDR, this.uniformWriter.float32, [source]);
  }

  protected onDispose(ctx: IRenderContext): void {
    if (this.pipelineHDR !== NULL_HANDLE) ctx.destroyPipeline(this.pipelineHDR);
    if (this.pipelineLDR !== NULL_HANDLE) ctx.destroyPipeline(this.pipelineLDR);
    if (this.shader !== NULL_HANDLE) ctx.destroyShader(this.shader);
    this.pipelineHDR = NULL_HANDLE;
    this.pipelineLDR = NULL_HANDLE;
    this.shader = NULL_HANDLE;
  }
}
